import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api } from "../api";

const MAX_ATTEMPTS = 8;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [statusObj, setStatusObj] = useState(null);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [done, setDone] = useState(false);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setError("Missing session id.");
      return;
    }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled || n > MAX_ATTEMPTS) {
        if (!cancelled && n > MAX_ATTEMPTS) setError("Status check timed out. Check your email.");
        return;
      }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setStatusObj(data);
        setAttempts(n);
        if (data.payment_status === "paid") {
          setDone(true);
          clear();
          return;
        }
        if (data.status === "expired") {
          setError("Payment expired. Try again.");
          return;
        }
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        if (!cancelled) setError("Could not retrieve payment status.");
      }
    };
    poll(1);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="bg-bone min-h-[80vh] flex items-center justify-center px-5 py-20" data-testid="checkout-success-page">
      <div className="max-w-2xl w-full border-4 border-asphalt bg-card-light shadow-hard-lg p-10 text-center">
        {error ? (
          <>
            <AlertTriangle className="w-12 h-12 text-terra mx-auto mb-4" />
            <h1 className="font-display text-4xl uppercase tracking-tighter mb-3">Snag in the wire</h1>
            <p className="font-body text-ink mb-6" data-testid="checkout-error-msg">{error}</p>
            <Link to="/cart" className="inline-block bg-terra text-bone border-2 border-asphalt px-6 py-3 font-display uppercase tracking-wider shadow-hard">
              Back to cart
            </Link>
          </>
        ) : done ? (
          <>
            <CheckCircle2 className="w-14 h-14 text-asphalt mx-auto mb-4" />
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-terra mb-2" data-testid="checkout-success-eyebrow">✦ Receipt of Goods</div>
            <h1 className="font-display text-5xl uppercase tracking-tighter mb-3">Much Obliged.</h1>
            <p className="font-western text-xl text-ink mb-6">
              Your stuff is on its way. We'll get the postal mule fueled up by Tuesday.
            </p>
            <div className="flex justify-center gap-3 flex-wrap">
              <Link to="/shop" className="border-2 border-asphalt px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] shadow-hard hover:bg-asphalt hover:text-bone transition-colors">
                Keep browsing
              </Link>
              <Link to="/" className="bg-asphalt text-bone border-2 border-asphalt px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] shadow-hard hover:bg-terra">
                Back to The Dog House
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-terra mb-3">✦ Confirming payment…</div>
            <h1 className="font-display text-4xl uppercase tracking-tighter mb-3">Hold your horses</h1>
            <p className="font-body text-ink">
              Status check {attempts}/{MAX_ATTEMPTS}. {statusObj?.payment_status === "unpaid" ? "Awaiting confirmation from Stripe…" : null}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
